import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Sun, Waves, Smartphone, ShoppingBag, Wind, ThermometerSun, Footprints, Shield, Droplets, Camera, Battery, Plane, Heart, AlertTriangle, CheckCircle, ExternalLink, Fish, Utensils, Sparkles, Video, Watch, Shirt, Backpack, Zap } from 'lucide-react';

// Amazon Affiliate ID
const AMAZON_TAG = 'netsyte01-21';
// Use Amazon search links instead of direct product links (more reliable)
const buildAmazonLink = (searchTerm) => `https://www.amazon.de/s?k=${encodeURIComponent(searchTerm)}&tag=${AMAZON_TAG}`;

// Images
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1751389922516-c7a862af90de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHx3aW5kc3VyZmluZyUyMHN1bm55JTIwb2NlYW58ZW58MHx8fHwxNzcxODMzNjU1fDA&ixlib=rb-4.1.0&q=85',
  redSea: 'https://images.unsplash.com/photo-1708711973253-2055a821d5e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxSZWQlMjBTZWElMjBFZ3lwdCUyMGJlYWNoJTIwdHVycXVvaXNlJTIwd2F0ZXJ8ZW58MHx8fHwxNzcxODMzNjUyfDA&ixlib=rb-4.1.0&q=85',
  beach: 'https://images.pexels.com/photos/10139934/pexels-photo-10139934.jpeg',
  packing: 'https://images.pexels.com/photos/8276910/pexels-photo-8276910.jpeg',
  essentials: 'https://images.unsplash.com/photo-1495745190033-64f95ea0d02f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxiZWFjaCUyMHRyYXZlbCUyMGVzc2VudGlhbHMlMjBzdWl0Y2FzZSUyMHBhY2tpbmd8ZW58MHx8fHwxNzcxODMzOTQ0fDA&ixlib=rb-4.1.0&q=85',
  snorkeling: 'https://images.unsplash.com/photo-1651871756929-09d7bde4e97d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxzbm9ya2VsaW5nJTIwdHJvcGljYWwlMjBmaXNoJTIwY29sb3JmdWwlMjB1bmRlcndhdGVyJTIwUmVkJTIwU2VhfGVufDB8fHx8MTc3MTg0MTUzOHww&ixlib=rb-4.1.0&q=85',
  coralReef: 'https://images.pexels.com/photos/18229777/pexels-photo-18229777.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  suitcase: 'https://images.unsplash.com/photo-1654355205410-30ca4ba0dc22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxiZWFjaCUyMHRyYXZlbCUyMGVzc2VudGlhbHMlMjBzdWl0Y2FzZSUyMHBhY2tpbmd8ZW58MHx8fHwxNzcxODMzOTQ0fDA&ixlib=rb-4.1.0&q=85',
};

const PackingList = () => {
  const { language } = useLanguage();

  const heroContent = {
    de: {
      title: 'Packliste Soma Bay',
      subtitle: 'Lerne aus den Fehlern anderer',
      intro: 'Nach Jahren Surfschule haben wir alles gesehen: Verbrannte Touristen, Seeigel-Opfer, ertrunkene Handys... Damit du nicht auf unserer "Wall of Shame" landest, hier die ehrliche Packliste – getestet an echten Gästen.'
    },
    en: {
      title: 'Packing List Soma Bay',
      subtitle: 'Learn from the mistakes of others',
      intro: 'After years of running a surf school, we\'ve seen it all: Sunburned tourists, sea urchin victims, drowned phones... So you don\'t end up on our "Wall of Shame", here\'s the honest packing list – tested on real guests.'
    }
  };

  const hallOfFame = {
    de: [
      {
        icon: '🦞',
        title: 'DER HUMMER-LOOK',
        quote: '"Ich creme mich im Wasser ja eh nicht ein..."',
        result: '→ 3 Tage später: Hotelzimmer statt Surfbrett',
        teamComment: 'Peter: "Jede Woche mindestens ein Hummer. Jede. Einzelne. Woche."',
        solution: 'Wasserfeste Sonnencreme LSF 50+',
        whyImportant: 'Die Sonne hier hat NULL Chill. Sie nimmt keine Gefangenen.',
        products: [
          { name: 'Sonnencreme LSF 50+ wasserfest', search: 'Sonnencreme LSF 50 wasserfest Sport' },
          { name: 'Zink Sonnencreme Gesicht', search: 'Zink Sonnencreme Gesicht Surfer' }
        ]
      },
      {
        icon: '🦔',
        title: 'DER SEEIGEL-TANZ',
        quote: '"Flip-Flops reichen doch!"',
        result: '→ Spoiler: Sie reichen nicht.',
        teamComment: 'Petra: "Der Satz \'Ich brauch keine Schuhe\' ist meist der letzte vor dem Schrei."',
        solution: 'Reef Boots / Neoprenschuhe',
        whyImportant: 'Unsere Statistik: 0 Seeigel-Unfälle mit Schuhen. 47 ohne. (Diese Saison)',
        products: [
          { name: 'Neoprenschuhe', search: 'Neoprenschuhe Wasserschuhe' },
          { name: 'Reef Boots', search: 'Reef Boots Tauchschuhe' }
        ]
      },
      {
        icon: '📱',
        title: 'DAS SALZWASSER-HANDY',
        quote: '"Ich halt es nur kurz hoch für ein Foto..."',
        result: '→ RIP iPhone',
        teamComment: 'Timo: "Ich hab aufgehört zu zählen wie viele Handys ich schon aus dem Wasser gefischt hab."',
        solution: 'Wasserdichte Handyhülle',
        whyImportant: 'Kostet 8€. Ein neues iPhone kostet... mehr.',
        products: [
          { name: 'Wasserdichte Handyhülle', search: 'wasserdichte Handyhülle universal' }
        ]
      }
    ],
    en: [
      {
        icon: '🦞',
        title: 'THE LOBSTER LOOK',
        quote: '"I don\'t need to apply sunscreen in the water..."',
        result: '→ 3 days later: Hotel room instead of surfboard',
        teamComment: 'Peter: "At least one lobster every week. Every. Single. Week."',
        solution: 'Waterproof Sunscreen SPF 50+',
        whyImportant: 'The sun here has ZERO chill. It takes no prisoners.',
        products: [
          { name: 'Sunscreen SPF 50+ waterproof', search: 'Sonnencreme LSF 50 wasserfest Sport' },
          { name: 'Zinc Sunscreen Face', search: 'Zink Sonnencreme Gesicht Surfer' }
        ]
      },
      {
        icon: '🦔',
        title: 'THE SEA URCHIN DANCE',
        quote: '"Flip-flops will be fine!"',
        result: '→ Spoiler: They won\'t.',
        teamComment: 'Petra: "The sentence \'I don\'t need shoes\' is usually the last one before the scream."',
        solution: 'Reef Boots / Neoprene Shoes',
        whyImportant: 'Our statistics: 0 sea urchin incidents with shoes. 47 without. (This season)',
        products: [
          { name: 'Neoprene Shoes', search: 'Neoprenschuhe Wasserschuhe' },
          { name: 'Reef Boots', search: 'Reef Boots Tauchschuhe' }
        ]
      },
      {
        icon: '📱',
        title: 'THE SALTWATER PHONE',
        quote: '"I\'ll just hold it up for a quick photo..."',
        result: '→ RIP iPhone',
        teamComment: 'Timo: "I\'ve stopped counting how many phones I\'ve fished out of the water."',
        solution: 'Waterproof Phone Case',
        whyImportant: 'Costs €8. A new iPhone costs... more.',
        products: [
          { name: 'Waterproof Phone Case', search: 'wasserdichte Handyhülle universal' }
        ]
      }
    ]
  };

  const categories = {
    de: [
      {
        icon: <Sun className="w-6 h-6" />,
        title: 'Sonnenschutz',
        subtitle: '(Endgegner Level 100)',
        description: 'Die Sonne in Ägypten spielt nicht. Pro-Tipp: Zwischen 12-14 Uhr bist du am besten im Schatten. Oder im Restaurant.',
        items: [
          { name: 'Sonnencreme LSF 50+ wasserfest', comment: 'Die gute, nicht die vom Discounter', search: 'Sonnencreme LSF 50 wasserfest Sport', must: true },
          { name: 'After-Sun Lotion', comment: 'Für den Fall, dass du nicht auf uns gehört hast', search: 'After Sun Lotion', must: false },
          { name: 'Rashguard / Lycra UV-Schutz', comment: 'Sieht auch noch cool aus', search: 'Rashguard Herren UV Schutz', must: true },
          { name: 'Surf-Cap mit Nackenschutz', comment: 'Glatze = Brandblasen. Trust us.', search: 'Surf Cap Nackenschutz UV', must: false }
        ]
      },
      {
        icon: <Waves className="w-6 h-6" />,
        title: 'Im Wasser',
        subtitle: '(Freund mit Geheimnissen)',
        description: 'Das Rote Meer ist wunderschön. Aber es hat Seeigel. Viele Seeigel. Und Korallen. Die sind scharf.',
        items: [
          { name: 'Neoprenschuhe / Reef Boots', comment: 'PFLICHT. Keine Diskussion.', search: 'Neoprenschuhe Wasserschuhe Erwachsene', must: true },
          { name: 'Neopren Shorty 2/3mm', comment: 'Wasser ist warm, aber Wind kann kühl sein', search: 'Neoprenanzug Shorty 3mm', must: false },
          { name: 'Wasserdichte Handyhülle', comment: '8€ vs. 1000€ fürs neue Handy', search: 'wasserdichte Handyhülle universal', must: true },
          { name: 'Schwimmende Sonnenbrille', comment: 'Normale Brille = weg für immer', search: 'schwimmende Sonnenbrille Sport', must: false }
        ]
      },
      {
        icon: <ShoppingBag className="w-6 h-6" />,
        title: 'Praktisches',
        subtitle: '(Macht das Leben schöner)',
        description: 'Kein Muss, aber du wirst froh sein wenn du es dabei hast.',
        items: [
          { name: 'Dry Bag', comment: 'Für Schlüssel, Geld und das Zeug das nicht nass werden sollte', search: 'Dry Bag wasserdicht', must: false },
          { name: 'Mikrofaser-Handtuch', comment: 'Schnell trocken, leicht, kein Sand-Magnet', search: 'Mikrofaser Handtuch schnelltrocknend', must: false },
          { name: 'Action Cam / GoPro', comment: 'Für die Insta-Stories die niemand fragt', search: 'Action Kamera wasserdicht', must: false },
          { name: 'Ohrstöpsel Surfer\'s Ear', comment: 'Schützt vor Wind & Wasser im Ohr', search: 'Ohrstöpsel Schwimmen wasserdicht', must: false }
        ]
      },
      {
        icon: <Heart className="w-6 h-6" />,
        title: 'Erste Hilfe',
        subtitle: '(Hoffst du brauchst du nicht)',
        description: 'Kleine Schnitte von Korallen, aufgeschürfte Knie... passiert. Sei vorbereitet.',
        items: [
          { name: 'Wasserfeste Pflaster', comment: 'Normale Pflaster halten 0 Sekunden', search: 'wasserfeste Pflaster', must: true },
          { name: 'Wund- und Heilsalbe', comment: 'Für die kleinen Korallen-Souvenirs', search: 'Bepanthen Wund und Heilsalbe', must: false },
          { name: 'Elektrolyt-Tabletten', comment: 'Gegen Dehydrierung nach langen Sessions', search: 'Elektrolyt Tabletten Sport', must: false },
          { name: 'Blasenpflaster', comment: 'Neue Neoprenschuhe + nasse Füße = Blasen', search: 'Blasenpflaster Compeed', must: false }
        ]
      },
      {
        icon: <Plane className="w-6 h-6" />,
        title: 'Reise-Basics',
        subtitle: '(Fast vergessen!)',
        description: 'Die langweiligen Sachen die man immer vergisst.',
        items: [
          { name: 'Reiseadapter', comment: 'Ägypten hat andere Steckdosen', search: 'Reiseadapter universal', must: true },
          { name: 'Powerbank', comment: 'Handy laden am Strand = unmöglich', search: 'Powerbank 20000mAh', must: false },
          { name: 'Trinkflasche isoliert', comment: 'Warmes Wasser bei 40°C = bäh', search: 'Trinkflasche Edelstahl isoliert', must: false },
          { name: 'Leichte Windjacke', comment: 'Abende können überraschend frisch sein', search: 'leichte Windjacke packbar', must: false }
        ]
      },
      {
        icon: <Fish className="w-6 h-6" />,
        title: 'Schnorcheln & Tauchen',
        subtitle: '(Unterwasserwelt entdecken)',
        description: 'Das Rote Meer hat eine der schönsten Unterwasserwelten der Welt. Nutze sie!',
        items: [
          { name: 'Schnorchelmaske', comment: 'Eigene passt besser als geliehene', search: 'Schnorchelmaske Erwachsene', must: false },
          { name: 'Reef-Safe Sonnencreme', comment: 'Schützt die Korallen', search: 'reef safe Sonnencreme', must: false },
          { name: 'Unterwasserkamera', comment: 'Die Fische sind es wert', search: 'Unterwasserkamera', must: false },
          { name: 'Anti-Fog Spray', comment: 'Beschlagene Maske = kein Spaß', search: 'Anti Fog Spray Taucherbrille', must: false }
        ]
      },
      {
        icon: <Sparkles className="w-6 h-6" />,
        title: 'Beauty & Pflege',
        subtitle: '(Salzwasser-Survival)',
        description: 'Sonne, Salz und Wind sind brutal zu Haut und Haaren. Sei vorbereitet.',
        items: [
          { name: 'Leave-In Conditioner', comment: 'Salzwasser zerstört die Haare', search: 'Leave In Conditioner Salzwasser', must: false },
          { name: 'Lippenpflege mit LSF', comment: 'Verbrannte Lippen tun WEH', search: 'Lippenpflege LSF 30', must: true },
          { name: 'Aloe Vera Gel', comment: 'Das Wundermittel bei Sonnenbrand', search: 'Aloe Vera Gel pur', must: false },
          { name: 'Feuchtigkeitscreme', comment: 'Gegen die Salzwasser-Kruste', search: 'Feuchtigkeitscreme Gesicht', must: false }
        ]
      },
      {
        icon: <Utensils className="w-6 h-6" />,
        title: 'Snacks & Energie',
        subtitle: '(Für zwischendurch)',
        description: 'Surfen macht hungrig. Sehr hungrig. Und der nächste Kiosk ist weit.',
        items: [
          { name: 'Müsliriegel / Energieriegel', comment: 'Schnelle Energie zwischen Sessions', search: 'Energieriegel Sport', must: false },
          { name: 'Nüsse & Trockenfrüchte', comment: 'Gesunder Snack am Strand', search: 'Nüsse Trockenfrüchte Mix', must: false },
          { name: 'Kaugummi', comment: 'Für den Druckausgleich beim Fliegen', search: 'Kaugummi zuckerfrei', must: false },
          { name: 'Reise-Besteck', comment: 'Für spontane Picknicks', search: 'Reisebesteck Set', must: false }
        ]
      },
      {
        icon: <Video className="w-6 h-6" />,
        title: 'Action Cams & Zubehör',
        subtitle: '(Für epische Aufnahmen)',
        description: 'Dein erster Wasserstart? Der muss dokumentiert werden! Und später kannst du damit angeben.',
        items: [
          { name: 'GoPro Hero 13', comment: 'Der Klassiker – wasserdicht & bombensicher', search: 'GoPro Hero 13 Black', must: false },
          { name: 'DJI Osmo Action 5', comment: 'Längere Akkulaufzeit, tolle Bildstabilisierung', search: 'DJI Osmo Action 5 Pro', must: false },
          { name: 'Insta360 X4', comment: '360°-Videos für den Wow-Effekt', search: 'Insta360 X4', must: false },
          { name: 'Unterwasser-Dome', comment: 'Half-underwater Shots wie aus dem Magazin', search: 'GoPro Dome Unterwasser', must: false },
          { name: 'Mouth Mount', comment: 'Freihändig filmen beim Surfen', search: 'GoPro Mouth Mount Surfen', must: false },
          { name: 'Helm Mount / Head Strap', comment: 'POV-Perspektive aus deiner Sicht', search: 'GoPro Head Strap Mount', must: false }
        ]
      },
      {
        icon: <Watch className="w-6 h-6" />,
        title: 'Technik & Gadgets',
        subtitle: '(Für Technik-Nerds)',
        description: 'Wer braucht schon Bargeld, wenn man eine Smartwatch hat? Spoiler: Am Strand alle.',
        items: [
          { name: 'Garmin Instinct Solar', comment: 'GPS-Sportuhr, lädt mit Sonne – nie wieder Akku leer', search: 'Garmin Instinct Solar 2', must: false },
          { name: 'Apple Watch Ultra 2', comment: 'Wasserdicht bis 100m, Wind-Apps, Angeber-Faktor', search: 'Apple Watch Ultra 2', must: false },
          { name: 'JBL Charge 5', comment: 'Wasserdichter Bluetooth-Speaker für die Strandparty', search: 'JBL Charge 5 Bluetooth Lautsprecher', must: false },
          { name: 'Solar-Powerbank', comment: 'Handy laden ohne Steckdose – die Sonne arbeitet für dich', search: 'Solar Powerbank 30000mAh', must: false },
          { name: 'Wasserdichte Kopfhörer', comment: 'Musik beim Schwimmen? Ja, das geht!', search: 'wasserdichte Kopfhörer Schwimmen', must: false }
        ]
      },
      {
        icon: <Shield className="w-6 h-6" />,
        title: 'Surf-Equipment',
        subtitle: '(Für echte Surfer)',
        description: 'Klar, das meiste kannst du vor Ort leihen. Aber manche Sachen solltest du selbst haben.',
        items: [
          { name: 'Impact Vest / Prallschutzweste', comment: 'Sicherheit bei Stürzen, wärmt auch', search: 'Impact Vest Wassersport Prallschutz', must: false },
          { name: 'Neopren-Schuhe', comment: 'Schutz vor Seeigeln & heißem Sand', search: 'Neoprenschuhe Surfen', must: true },
          { name: 'Surf-Poncho', comment: 'Umziehen am Strand mit Stil', search: 'Surf Poncho Erwachsene', must: false },
          { name: 'Dry Bag', comment: 'Wertsachen sicher & trocken', search: 'Dry Bag wasserdicht 20L', must: true },
          { name: 'Lycra UV-Schutz', comment: 'Sonnenschutz + weniger Wachs auf der Haut', search: 'Rashguard Herren Langarm UV', must: false },
          { name: 'Trapez-Shorts', comment: 'Falls du dein eigenes Trapez hast', search: 'Trapez Shorts Windsurfen', must: false }
        ]
      },
      {
        icon: <Zap className="w-6 h-6" />,
        title: 'Luxus & Spielzeug',
        subtitle: '(Wenn Geld keine Rolle spielt)',
        description: 'Okay, das hier BRAUCHT niemand. Aber haben wollen ist auch okay.',
        items: [
          { name: 'Unterwasser-Scooter', comment: 'Schnorcheln mit Speed – mega Spaß', search: 'Unterwasser Scooter Seabob', must: false },
          { name: 'Oakley Prizm Polarized', comment: 'Weniger Blendung, mehr Style', search: 'Oakley Prizm Polarized Sport', must: false },
          { name: 'Garmin Fenix 8', comment: 'Die ultimative Outdoor-Uhr', search: 'Garmin Fenix 8 Solar', must: false },
          { name: 'Surfskate', comment: 'Training für Tage ohne Wind', search: 'Surfskate Carver', must: false }
        ]
      }
    ],
    en: [
      {
        icon: <Sun className="w-6 h-6" />,
        title: 'Sun Protection',
        subtitle: '(Final Boss Level 100)',
        description: 'The sun in Egypt doesn\'t play. Pro tip: Between 12-2pm you\'re best off in the shade. Or in a restaurant.',
        items: [
          { name: 'Sunscreen SPF 50+ waterproof', comment: 'The good stuff, not the discount one', search: 'Sonnencreme LSF 50 wasserfest Sport', must: true },
          { name: 'After-Sun Lotion', comment: 'For when you didn\'t listen to us', search: 'After Sun Lotion', must: false },
          { name: 'Rashguard / UV Lycra', comment: 'Also looks cool', search: 'Rashguard Herren UV Schutz', must: true },
          { name: 'Surf Cap with neck protection', comment: 'Bald head = blisters. Trust us.', search: 'Surf Cap Nackenschutz UV', must: false }
        ]
      },
      {
        icon: <Waves className="w-6 h-6" />,
        title: 'In the Water',
        subtitle: '(Friend with secrets)',
        description: 'The Red Sea is beautiful. But it has sea urchins. Lots of them. And coral. It\'s sharp.',
        items: [
          { name: 'Neoprene Shoes / Reef Boots', comment: 'MANDATORY. No discussion.', search: 'Neoprenschuhe Wasserschuhe Erwachsene', must: true },
          { name: 'Neoprene Shorty 2/3mm', comment: 'Water is warm, but wind can be cool', search: 'Neoprenanzug Shorty 3mm', must: false },
          { name: 'Waterproof Phone Case', comment: '€8 vs. €1000 for a new phone', search: 'wasserdichte Handyhülle universal', must: true },
          { name: 'Floating Sunglasses', comment: 'Regular glasses = gone forever', search: 'schwimmende Sonnenbrille Sport', must: false }
        ]
      },
      {
        icon: <ShoppingBag className="w-6 h-6" />,
        title: 'Practical Stuff',
        subtitle: '(Makes life better)',
        description: 'Not a must, but you\'ll be glad you brought it.',
        items: [
          { name: 'Dry Bag', comment: 'For keys, money and stuff that shouldn\'t get wet', search: 'Dry Bag wasserdicht', must: false },
          { name: 'Microfiber Towel', comment: 'Dries fast, light, no sand magnet', search: 'Mikrofaser Handtuch schnelltrocknend', must: false },
          { name: 'Action Cam / GoPro', comment: 'For the Insta stories nobody asked for', search: 'Action Kamera wasserdicht', must: false },
          { name: 'Earplugs Surfer\'s Ear', comment: 'Protects from wind & water in ear', search: 'Ohrstöpsel Schwimmen wasserdicht', must: false }
        ]
      },
      {
        icon: <Heart className="w-6 h-6" />,
        title: 'First Aid',
        subtitle: '(Hope you won\'t need it)',
        description: 'Small cuts from coral, scraped knees... happens. Be prepared.',
        items: [
          { name: 'Waterproof Band-Aids', comment: 'Regular ones last 0 seconds', search: 'wasserfeste Pflaster', must: true },
          { name: 'Wound Healing Ointment', comment: 'For the little coral souvenirs', search: 'Bepanthen Wund und Heilsalbe', must: false },
          { name: 'Electrolyte Tablets', comment: 'Against dehydration after long sessions', search: 'Elektrolyt Tabletten Sport', must: false },
          { name: 'Blister Plasters', comment: 'New neoprene shoes + wet feet = blisters', search: 'Blasenpflaster Compeed', must: false }
        ]
      },
      {
        icon: <Plane className="w-6 h-6" />,
        title: 'Travel Basics',
        subtitle: '(Almost forgot!)',
        description: 'The boring stuff you always forget.',
        items: [
          { name: 'Travel Adapter', comment: 'Egypt has different outlets', search: 'Reiseadapter universal', must: true },
          { name: 'Power Bank', comment: 'Charging phone at beach = impossible', search: 'Powerbank 20000mAh', must: false },
          { name: 'Insulated Water Bottle', comment: 'Warm water at 40°C = yuck', search: 'Trinkflasche Edelstahl isoliert', must: false },
          { name: 'Light Windbreaker', comment: 'Evenings can be surprisingly cool', search: 'leichte Windjacke packbar', must: false }
        ]
      },
      {
        icon: <Fish className="w-6 h-6" />,
        title: 'Snorkeling & Diving',
        subtitle: '(Discover the underwater world)',
        description: 'The Red Sea has one of the most beautiful underwater worlds. Make the most of it!',
        items: [
          { name: 'Snorkel Mask', comment: 'Your own fits better than a rental', search: 'Schnorchelmaske Erwachsene', must: false },
          { name: 'Reef-Safe Sunscreen', comment: 'Protects the corals', search: 'reef safe Sonnencreme', must: false },
          { name: 'Underwater Camera', comment: 'The fish are worth it', search: 'Unterwasserkamera', must: false },
          { name: 'Anti-Fog Spray', comment: 'Foggy mask = no fun', search: 'Anti Fog Spray Taucherbrille', must: false }
        ]
      },
      {
        icon: <Sparkles className="w-6 h-6" />,
        title: 'Beauty & Care',
        subtitle: '(Saltwater survival)',
        description: 'Sun, salt and wind are brutal on skin and hair. Be prepared.',
        items: [
          { name: 'Leave-In Conditioner', comment: 'Saltwater destroys your hair', search: 'Leave In Conditioner Salzwasser', must: false },
          { name: 'Lip Balm with SPF', comment: 'Sunburned lips HURT', search: 'Lippenpflege LSF 30', must: true },
          { name: 'Aloe Vera Gel', comment: 'The miracle cure for sunburn', search: 'Aloe Vera Gel pur', must: false },
          { name: 'Moisturizer', comment: 'Against the saltwater crust', search: 'Feuchtigkeitscreme Gesicht', must: false }
        ]
      },
      {
        icon: <Utensils className="w-6 h-6" />,
        title: 'Snacks & Energy',
        subtitle: '(For in between)',
        description: 'Surfing makes you hungry. Very hungry. And the nearest kiosk is far away.',
        items: [
          { name: 'Granola / Energy Bars', comment: 'Quick energy between sessions', search: 'Energieriegel Sport', must: false },
          { name: 'Nuts & Dried Fruits', comment: 'Healthy snack on the beach', search: 'Nüsse Trockenfrüchte Mix', must: false },
          { name: 'Chewing Gum', comment: 'For pressure equalization on the flight', search: 'Kaugummi zuckerfrei', must: false },
          { name: 'Travel Cutlery', comment: 'For spontaneous picnics', search: 'Reisebesteck Set', must: false }
        ]
      },
      {
        icon: <Video className="w-6 h-6" />,
        title: 'Action Cams & Accessories',
        subtitle: '(For epic footage)',
        description: 'Your first water start? That needs to be documented! And later you can show off with it.',
        items: [
          { name: 'GoPro Hero 13', comment: 'The classic – waterproof & bulletproof', search: 'GoPro Hero 13 Black', must: false },
          { name: 'DJI Osmo Action 5', comment: 'Longer battery life, great stabilization', search: 'DJI Osmo Action 5 Pro', must: false },
          { name: 'Insta360 X4', comment: '360° videos for the wow effect', search: 'Insta360 X4', must: false },
          { name: 'Underwater Dome', comment: 'Half-underwater shots like from a magazine', search: 'GoPro Dome Unterwasser', must: false },
          { name: 'Mouth Mount', comment: 'Hands-free filming while surfing', search: 'GoPro Mouth Mount Surfen', must: false },
          { name: 'Helmet Mount / Head Strap', comment: 'POV perspective from your view', search: 'GoPro Head Strap Mount', must: false }
        ]
      },
      {
        icon: <Watch className="w-6 h-6" />,
        title: 'Tech & Gadgets',
        subtitle: '(For tech nerds)',
        description: 'Who needs cash when you have a smartwatch? Spoiler: Everyone at the beach.',
        items: [
          { name: 'Garmin Instinct Solar', comment: 'GPS sports watch, solar charging – never empty', search: 'Garmin Instinct Solar 2', must: false },
          { name: 'Apple Watch Ultra 2', comment: 'Waterproof to 100m, wind apps, show-off factor', search: 'Apple Watch Ultra 2', must: false },
          { name: 'JBL Charge 5', comment: 'Waterproof Bluetooth speaker for beach parties', search: 'JBL Charge 5 Bluetooth Lautsprecher', must: false },
          { name: 'Solar Power Bank', comment: 'Charge your phone without outlets – the sun works for you', search: 'Solar Powerbank 30000mAh', must: false },
          { name: 'Waterproof Headphones', comment: 'Music while swimming? Yes, that works!', search: 'wasserdichte Kopfhörer Schwimmen', must: false }
        ]
      },
      {
        icon: <Shield className="w-6 h-6" />,
        title: 'Surf Equipment',
        subtitle: '(For real surfers)',
        description: 'Sure, you can rent most stuff on site. But some things you should bring yourself.',
        items: [
          { name: 'Impact Vest', comment: 'Safety for crashes, also keeps you warm', search: 'Impact Vest Wassersport Prallschutz', must: false },
          { name: 'Neoprene Shoes', comment: 'Protection from sea urchins & hot sand', search: 'Neoprenschuhe Surfen', must: true },
          { name: 'Surf Poncho', comment: 'Change at the beach with style', search: 'Surf Poncho Erwachsene', must: false },
          { name: 'Dry Bag', comment: 'Valuables safe & dry', search: 'Dry Bag wasserdicht 20L', must: true },
          { name: 'UV Lycra / Rashguard', comment: 'Sun protection + less wax on your skin', search: 'Rashguard Herren Langarm UV', must: false },
          { name: 'Harness Shorts', comment: 'If you have your own harness', search: 'Trapez Shorts Windsurfen', must: false }
        ]
      },
      {
        icon: <Zap className="w-6 h-6" />,
        title: 'Luxury & Toys',
        subtitle: '(When money is no object)',
        description: 'Okay, nobody NEEDS this. But wanting it is also okay.',
        items: [
          { name: 'Underwater Scooter', comment: 'Snorkeling with speed – mega fun', search: 'Unterwasser Scooter Seabob', must: false },
          { name: 'Oakley Prizm Polarized', comment: 'Less glare, more style', search: 'Oakley Prizm Polarized Sport', must: false },
          { name: 'Garmin Fenix 8', comment: 'The ultimate outdoor watch', search: 'Garmin Fenix 8 Solar', must: false },
          { name: 'Surfskate', comment: 'Training for days without wind', search: 'Surfskate Carver', must: false }
        ]
      }
    ]
  };

  const content = language === 'de' ? heroContent.de : heroContent.en;
  const fame = language === 'de' ? hallOfFame.de : hallOfFame.en;
  const cats = language === 'de' ? categories.de : categories.en;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="packing-list-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-4">🎒</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            {content.title}
          </h1>
          <p className="text-2xl md:text-3xl font-light mb-6 text-amber-100">
            {content.subtitle} 😅
          </p>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            {content.intro}
          </p>
        </div>
      </section>

      {/* Hall of Fame Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-4xl">🏆</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-4">
              {language === 'de' ? 'Hall of Fame: Die größten Klassiker' : 'Hall of Fame: The Greatest Hits'}
            </h2>
            <p className="text-gray-600 mt-2">
              {language === 'de' ? 'Echte Geschichten. Echte Schmerzen. Echte Lerneffekte.' : 'Real stories. Real pain. Real lessons learned.'}
            </p>
          </div>

          <div className="space-y-8">
            {fame.map((item, index) => (
              <div key={index} className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border-l-4 border-amber-400 shadow-lg">
                <div className="flex items-start gap-4">
                  <span className="text-5xl">{item.icon}</span>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 italic mt-1">{item.quote}</p>
                    <p className="text-red-600 font-medium">{item.result}</p>
                    
                    <div className="mt-4 bg-white/70 rounded-lg p-4">
                      <p className="text-amber-800 text-sm italic">"{item.teamComment.split(': ')[1]}"</p>
                      <p className="text-amber-600 text-sm font-medium">— {item.teamComment.split(': ')[0]}</p>
                    </div>

                    <div className="mt-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-gray-900">{language === 'de' ? 'Lösung' : 'Solution'}:</span>
                      <span className="text-gray-700">{item.solution}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{item.whyImportant}</p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {item.products.map((product, pIndex) => (
                        <a
                          key={pIndex}
                          href={buildAmazonLink(product.search)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1 rounded-full transition-colors"
                        >
                          {product.name}
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beach Lifestyle Image Break */}
      <section className="relative h-64 md:h-80 overflow-hidden">
        <img 
          src={IMAGES.essentials} 
          alt={language === 'de' ? 'Reise-Essentials am Strand' : 'Travel essentials on the beach'} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-100 to-transparent"></div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              {language === 'de' ? 'Die komplette Packliste' : 'The Complete Packing List'}
            </h2>
            <p className="text-gray-600 mt-2">
              {language === 'de' ? 'Alles was du brauchst – kategorisiert und kommentiert' : 'Everything you need – categorized and commented'}
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <span className="inline-flex items-center text-sm bg-red-100 text-red-700 px-3 py-1 rounded-full">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {language === 'de' ? 'MUSS MIT' : 'MUST HAVE'}
              </span>
              <span className="inline-flex items-center text-sm bg-gray-200 text-gray-700 px-3 py-1 rounded-full">
                {language === 'de' ? 'Nice to have' : 'Nice to have'}
              </span>
            </div>
          </div>

          {/* First half of categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {cats.slice(0, Math.ceil(cats.length / 2)).map((category, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4 text-white">
                  <div className="flex items-center gap-3">
                    {category.icon}
                    <div>
                      <h3 className="font-bold text-lg">{category.title}</h3>
                      <p className="text-cyan-100 text-sm">{category.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-gray-600 text-sm mb-4">{category.description}</p>
                  <ul className="space-y-3">
                    {category.items.map((item, iIndex) => (
                      <li key={iIndex} className="flex items-start gap-2">
                        <div className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.must ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'}`}>
                          {item.must ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                        </div>
                        <div className="flex-1">
                          <a
                            href={buildAmazonLink(item.search)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-gray-900 hover:text-cyan-600 transition-colors"
                          >
                            {item.name}
                          </a>
                          <p className="text-xs text-gray-500 italic">{item.comment}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Snorkeling Image Break */}
          <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-8 shadow-lg">
            <img 
              src={IMAGES.snorkeling} 
              alt={language === 'de' ? 'Schnorcheln im Roten Meer' : 'Snorkeling in the Red Sea'} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end">
              <p className="text-white text-lg md:text-xl font-medium p-6">
                {language === 'de' 
                  ? '🐠 Das Rote Meer wartet auf dich – mit der richtigen Ausrüstung!' 
                  : '🐠 The Red Sea awaits – with the right equipment!'}
              </p>
            </div>
          </div>

          {/* Second half of categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cats.slice(Math.ceil(cats.length / 2)).map((category, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4 text-white">
                  <div className="flex items-center gap-3">
                    {category.icon}
                    <div>
                      <h3 className="font-bold text-lg">{category.title}</h3>
                      <p className="text-cyan-100 text-sm">{category.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-gray-600 text-sm mb-4">{category.description}</p>
                  <ul className="space-y-3">
                    {category.items.map((item, iIndex) => (
                      <li key={iIndex} className="flex items-start gap-2">
                        <div className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.must ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'}`}>
                          {item.must ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                        </div>
                        <div className="flex-1">
                          <a
                            href={buildAmazonLink(item.search)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-gray-900 hover:text-cyan-600 transition-colors"
                          >
                            {item.name}
                          </a>
                          <p className="text-xs text-gray-500 italic">{item.comment}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Insurance Section */}
      <section className="py-12 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-4xl">🛡️</span>
            <h2 className="text-2xl font-bold text-gray-900 mt-4">
              {language === 'de' ? 'Fast vergessen: Reiseversicherung!' : 'Almost forgot: Travel Insurance!'}
            </h2>
            <p className="text-gray-600 mt-2">
              {language === 'de' 
                ? 'Koffer gepackt, Vorfreude riesig – und dann? Flug storniert, Magen-Darm aus dem Nichts, oder der Koffer macht Urlaub ohne dich. Eine gute Versicherung kostet weniger als ein Abendessen, rettet aber den ganzen Urlaub.' 
                : 'Bags packed, excitement huge – and then? Flight cancelled, stomach bug out of nowhere, or your luggage takes a vacation without you. Good insurance costs less than dinner but saves the whole trip.'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Reiserücktritt */}
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-3">✈️</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                {language === 'de' ? 'Reiserücktrittsversicherung' : 'Trip Cancellation Insurance'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {language === 'de' 
                  ? 'Wenn was dazwischen kommt – Geld zurück statt Tränen. Schützt bei Krankheit, Unfall oder Jobverlust.' 
                  : 'When something comes up – money back instead of tears. Protects against illness, accident or job loss.'}
              </p>
              <a 
                rel="sponsored" 
                href="https://www.awin1.com/cread.php?s=3338732&v=11705&q=329249&r=2786628"
                target="_blank"
                className="inline-block bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg"
              >
                {language === 'de' ? 'Jetzt absichern →' : 'Get protected →'}
              </a>
              <p className="text-xs text-gray-400 mt-3">HanseMerkur</p>
            </div>

            {/* Reisejahresschutz */}
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-3">🌍</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                {language === 'de' ? 'Reise-Jahresschutz' : 'Annual Travel Protection'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {language === 'de' 
                  ? 'Für Vielreisende – einmal abschließen, das ganze Jahr geschützt. Alle Reisen inklusive!' 
                  : 'For frequent travelers – sign up once, protected all year. All trips included!'}
              </p>
              <a 
                rel="sponsored" 
                href="https://www.awin1.com/cread.php?s=3338901&v=11705&q=329248&r=2786628"
                target="_blank"
                className="inline-block bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-6 py-3 rounded-full font-semibold hover:from-blue-600 hover:to-cyan-700 transition-all shadow-md hover:shadow-lg"
              >
                {language === 'de' ? 'Jahresschutz ansehen →' : 'View annual plan →'}
              </a>
              <p className="text-xs text-gray-400 mt-3">HanseMerkur</p>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            {language === 'de' 
              ? 'Unser Tipp: HanseMerkur – seit über 50 Jahren einer der führenden Reiseversicherer in Deutschland.' 
              : 'Our tip: HanseMerkur – one of the leading travel insurers in Germany for over 50 years.'}
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 bg-gradient-to-r from-cyan-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {language === 'de' ? 'Packliste gecheckt?' : 'Packing list checked?'}
          </h2>
          <p className="mb-6 text-cyan-100">
            {language === 'de' 
              ? 'Dann fehlt nur noch eins: Deine Vorab-Registrierung!' 
              : 'Then only one thing is missing: Your pre-registration!'}
          </p>
          <a
            href="https://reservations.surfmotion.com/reservation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white text-cyan-700 px-8 py-3 rounded-full font-semibold hover:bg-cyan-50 transition-colors"
          >
            {language === 'de' ? 'Jetzt registrieren' : 'Register Now'}
            <ExternalLink className="w-5 h-5 ml-2" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default PackingList;
